import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Divider,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import api from '../api/axios';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const PrivacyPolicy = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const removeAllVotes = async () => {
    if (!window.confirm('Remove all your votes? This cannot be undone.')) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const response = await api.delete('/api/privacy/votes/');
      setResult(response.data);
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('voted_')) localStorage.removeItem(key);
      });
    } catch (err) {
      setError('Failed to remove votes. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ pt: 10 }}>
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography
          variant="overline"
          sx={{ color: 'text.secondary', letterSpacing: '0.12em', fontSize: '0.7rem' }}
        >
          Legal
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, mb: 1 }}>
          Privacy Policy
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
          Last updated:{' '}
          {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
        </Typography>

        <Divider sx={{ mb: 5 }} />

        {/* Data controls */}
        <Box
          sx={{
            mb: 5,
            p: 3,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'rgba(201,146,42,0.05)',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.75 }}>
            Your Data Controls
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, lineHeight: 1.7 }}>
          We store a small amount of data only to prevent duplicate voting. Removing your
            votes also permanently deletes that data from our database {'–'} no trace remains.
          </Typography>
          <Button
            variant="contained"
            color="error"
            onClick={removeAllVotes}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ borderRadius: 1.5, mb: result || error ? 2 : 0 }}
          >
            {loading ? 'Removing...' : 'Remove All My Votes & Data'}
          </Button>
          {result && (
            <Alert severity="success" sx={{ borderRadius: 1.5 }}>
              {result.message}
              {result.deleted_count > 0 && ` Removed ${result.deleted_count} vote(s).`}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ borderRadius: 1.5 }}>
              {error}
            </Alert>
          )}
        </Box>

        <Section title="1. Introduction">
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            Welcome to Fallon d&apos;Floor. This Privacy Policy explains how we collect, use, and
            protect your information when you use our website.
          </Typography>
        </Section>

        <Section title="2. Information We Collect">
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 1 }}>
            <strong>Voting Data:</strong> When you vote, we temporarily store a small amount of
            data to prevent duplicate votes and maintain fairness.
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 1 }}>
            <strong>Local Storage:</strong> We use your browser&apos;s local storage to remember
            which nominations you&apos;ve voted on.
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            <strong>Analytics:</strong> We collect anonymous usage data to help improve the site.
          </Typography>
        </Section>

        <Section title="3. How We Use Your Information">
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            Your data is used solely to prevent duplicate voting. We do not use it for
            tracking, marketing, or any other purpose.
          </Typography>
        </Section>

        <Section title="4. Data Retention &amp; Deletion">
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            Data is stored only as long as needed to prevent duplicate voting. You can
            delete your data at any time using the controls above.
          </Typography>
        </Section>

        <Section title="5. Your Rights">
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            Under GDPR and similar regulations, you have the right to access, rectify, or erase
            personal data we hold. Use the button above to exercise your right to erasure.
          </Typography>
        </Section>

        
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;

